import React, { useState, useEffect, useCallback, useRef } from 'react';
import Topbar from '../components/Topbar';
import { useNavigate } from 'react-router-dom';
import { getUsers, deleteUser, toggleUserStatus, getUserActivities, sendUserNotification } from '../services/userService';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';
import { useRefresh } from '../utils/RefreshContext';
import { forgotPassword, verifyOtp, resendOtp as resendOtpApi, resetPassword } from '../services/userPasswordResetService';
const statusColor = { 1: 'active', 2: 'inactive', 0: 'deleted' };
const statusLabel = { 1: 'Active', 2: 'Inactive', 0: 'Deleted' };
const typeColor = { 0: 'free', 1: 'premium', 'Free': 'free', 'Premium': 'premium' };
const typeLabel = { 0: 'Free', 1: 'Premium', 'Free': 'Free', 'Premium': 'Premium' };

const UserManagement = () => {
    const navigate = useNavigate();
    const { refreshKey } = useRefresh();
    const [view, setView] = useState('list');
    const [usersList, setUsersList] = useState([]);
    const [meta, setMeta] = useState({ total: 0, totalPages: 1, currentPage: 1, limit: 10 });
    const [isLoading, setIsLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userToDelete, setUserToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isTogglingStatus, setIsTogglingStatus] = useState(false);
    const [filters, setFilters] = useState({
        page: 1,
        limit: 10,
        search: '',
        sortBy: 'full_name',
        sortOrder: 'asc',
        status: '',
        type: '',
        startDate: '',
        endDate: ''
    });

    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
    const [resetStep, setResetStep] = useState(1);
    const [resetOtp, setResetOtp] = useState(['', '', '', '', '', '']);
    const [resetTimer, setResetTimer] = useState(60);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPass, setShowPass] = useState(false);
    const [showConfirmPass, setShowConfirmPass] = useState(false);
    const [resetMessage, setResetMessage] = useState(null);
    const [isResetLoading, setIsResetLoading] = useState(false);

    const [activities, setActivities] = useState([]);
    const [activitiesLoading, setActivitiesLoading] = useState(false);
    const [activitiesMeta, setActivitiesMeta] = useState({ total: 0, totalPages: 1, currentPage: 1, limit: 10 });
    const [activitiesPage, setActivitiesPage] = useState(1);

    const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
    const [notificationTitle, setNotificationTitle] = useState('');
    const [notificationMessage, setNotificationMessage] = useState('');
    const [isSendingNotification, setIsSendingNotification] = useState(false);

    useEffect(() => {
        let interval;
        if (resetStep === 2 && resetTimer > 0) {
            interval = setInterval(() => {
                setResetTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [resetStep, resetTimer]);

    useEffect(() => {
        if (selectedUser && selectedUser.id) {
            fetchActivities(1);
        }
    }, [selectedUser]);

    const fetchActivities = async (page = 1) => {
        if (!selectedUser || !selectedUser.id) return;
        
        setActivitiesLoading(true);
        try {
            const response = await getUserActivities(selectedUser.id, { 
                page, 
                limit: 10
            });
            
            if (response) {
                const activitiesData = response.data || response || [];
                const meta = response.meta || {};
                
                setActivities(Array.isArray(activitiesData) ? activitiesData : []);
                setActivitiesMeta({
                    total: meta.total || response.total || 0,
                    totalPages: meta.totalPages || response.totalPages || 1,
                    currentPage: page,
                    limit: 10
                });
            }
        } catch (error) {
            console.error('Failed to fetch activities:', error);
            setActivities([]);
        } finally {
            setActivitiesLoading(false);
        }
    };
    
    const handleSendNotification = async (e) => {
        e.preventDefault();
        
        if (!notificationTitle.trim()) {
            toast.error('Please enter a title');
            return;
        }
        
        if (!notificationMessage.trim()) {
            toast.error('Please enter a message');
            return;
        }
        
        if (!selectedUser || !selectedUser.id) {
            toast.error('No user selected');
            return;
        }
        
        try {
            setIsSendingNotification(true);
            await sendUserNotification(selectedUser.id, {
                title: notificationTitle,
                message: notificationMessage
            });
            
            toast.success('Notification sent successfully!');
            setNotificationTitle('');
            setNotificationMessage('');
            setIsNotificationModalOpen(false);
        } catch (error) {
            toast.error('Failed to send notification');
            console.error('Error:', error);
        } finally {
            setIsSendingNotification(false);
        }
    };
    
    const closeNotificationModal = () => {
        setIsNotificationModalOpen(false);
        setNotificationTitle('');
        setNotificationMessage('');
    };

    const handleOtpChange = (element, index) => {
        if (isNaN(element.value)) return false;
        const newOtp = [...resetOtp];
        newOtp[index] = element.value;
        setResetOtp(newOtp);
        if (element.nextSibling && element.value !== "") {
            element.nextSibling.focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace" && !resetOtp[index] && e.target.previousSibling) {
            e.target.previousSibling.focus();
        }
    };

    const formatTimer = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = time % 60;
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    const handleResend = async () => {
        if (resetTimer === 0 && selectedUser) {
            setResetMessage(null);
            try {
                setIsResetLoading(true);
                await resendOtpApi(selectedUser.email);
                setResetTimer(60);
                setResetMessage({ type: 'success', text: "Verification code resent successfully!" });
            } catch (error) {
                setResetMessage({ type: 'error', text: "Failed to resend code. Please try again." });
            } finally {
                setIsResetLoading(false);
            }
        }
    };

    const handleForgotPassword = async (e) => {
        if (e) e.preventDefault();
        setResetMessage(null);
        if (!selectedUser || !selectedUser.email) {
            setResetMessage({ type: 'error', text: "No email available for this user." });
            return;
        }
        try {
            setIsResetLoading(true);
            await forgotPassword(selectedUser.email);
            setResetStep(2);
            setResetTimer(60);
            setResetMessage({ type: 'success', text: "Verification code sent to user's email!" });
        } catch (error) {
            setResetMessage({ type: 'error', text: "Failed to send verification code." });
        } finally {
            setIsResetLoading(false);
        }
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        setResetMessage(null);
        const otpCode = resetOtp.join('');
        if (otpCode.length < 6) {
            setResetMessage({ type: 'error', text: "Please enter the full 6-digit code." });
            return;
        }
        try {
            setIsResetLoading(true);
            const response = await verifyOtp(selectedUser.email, otpCode);
            if (response.data.success || response.status === 200 || response.data.message?.includes("verified")) {
                setResetStep(3);
                setResetMessage({ type: 'success', text: "Email verified successfully!" });
            }
        } catch (error) {
            setResetMessage({ type: 'error', text: "Invalid verification code. Please try again." });
        } finally {
            setIsResetLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setResetMessage(null);
        if (newPassword.length < 8) {
            setResetMessage({ type: 'error', text: "Password must be at least 8 characters long." });
            return;
        }
        if (newPassword !== confirmPassword) {
            setResetMessage({ type: 'error', text: "Passwords do not match." });
            return;
        }
        try {
            setIsResetLoading(true);
            await resetPassword(selectedUser.email, newPassword, confirmPassword);
            toast.success("Password reset successfully!");
            closeResetModal();
        } catch (error) {
            setResetMessage({ type: 'error', text: "Failed to reset password." });
        } finally {
            setIsResetLoading(false);
        }
    };

    const closeResetModal = () => {
        setIsResetPasswordModalOpen(false);
        setResetStep(1);
        setResetOtp(['', '', '', '', '', '']);
        setNewPassword('');
        setConfirmPassword('');
        setShowPass(false);
        setShowConfirmPass(false);
        setResetMessage(null);
    };

    const modalInputStyle = {
        width: '100%', padding: '12px 14px',
        background: '#F8FAFC',
        border: '1.5px solid #E8EDF3',
        borderRadius: 8, fontSize: 14,
        color: 'var(--navy)', outline: 'none',
        fontFamily: "'Space Grotesk', sans-serif"
    };

    const modalButtonStyle = {
        width: '100%', padding: '12px',
        background: isResetLoading ? 'rgba(102,178,255,0.5)' : 'var(--blue)',
        border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
        color: 'white', cursor: isResetLoading ? 'not-allowed' : 'pointer',
        fontFamily: "'Space Grotesk', sans-serif",
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        transition: 'all 0.2s', marginTop: 16
    };

    const fetchUsersData = useCallback(async (params = filters) => {
        setIsLoading(true);
        try {
            const response = await getUsers(params);
            if (response && response.data) {
                setUsersList(response.data);
                setMeta(response.meta);
            }
        } catch (error) {
            console.log('Fetch users error:', error);
            // toast.error("Failed to fetch users");
        } finally {
            setIsLoading(false);
        }
    }, []); // Explicitly decoupled from filters state

    const prevSearch = useRef(filters.search);

    // Unified Fetch Effect with Search Debounce
    useEffect(() => {
        const searchChanged = prevSearch.current !== filters.search;
        prevSearch.current = filters.search;

        const timer = setTimeout(() => {
            fetchUsersData(filters);
        }, searchChanged ? 500 : 0);

        return () => clearTimeout(timer);
    }, [filters, fetchUsersData, refreshKey]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value, page: key === 'page' ? value : 1 }));
    };

    const handleDeleteConfirmed = async () => {
        if (!userToDelete) return;
        setIsLoading(true);
        try {
            await deleteUser(userToDelete.id);
            toast.success("User deleted successfully");
            setUserToDelete(null);
            setSelectedUser(null);
            setView('list');
            fetchUsersData();
        } catch (error) {
            console.log('Delete user error:', error);
            // toast.error("Failed to delete user");
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggleUserStatus = async (user) => {
        if (!user || typeof user.status === 'undefined') return;
        const newStatus = user.status === 1 ? 2 : 1;
        setIsTogglingStatus(true);
        try {
            const response = await toggleUserStatus({ id: user.id, status: newStatus });
            if (response && (response.result || response.status === 1 || response.status === 200)) {
                toast.success(`User ${newStatus === 1 ? 'activated' : 'deactivated'} successfully`);
                if (selectedUser && selectedUser.id === user.id) {
                    setSelectedUser(prev => ({ ...prev, status: newStatus }));
                }
                setUsersList(prev => prev.map(u => u.id === user.id ? { ...u, status: newStatus } : u));
            } else {
                toast.error(response?.message || 'Failed to update user status');
            }
        } catch (error) {
            console.log('Toggle user status error:', error);
            // toast.error('Error updating user status');
        } finally {
            setIsTogglingStatus(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const getInitials = (name) => {
        if (!name) return '??';
        return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
    };

    const handleExportCSV = () => {
        if (!usersList || usersList.length === 0) {
            toast.info("No data to export");
            return;
        }

        const headers = ['Full Name', 'Email', 'Phone Number', 'Account Type', 'Status', 'Provider', 'Email Verified', 'Signup Date'];
        const csvRows = [headers.join(',')];

        usersList.forEach(u => {
            const row = [
                `"${u.full_name || ''}"`,
                `"${u.email || ''}"`,
                `"${u.phone_number || ''}"`,
                `"${u.type || typeLabel[u.account_type] || 'Free'}"`,
                `"${statusLabel[u.status] || 'Active'}"`,
                `"${u.provider_name || 'local'}"`,
                `"${u.is_email_verified ? 'Yes' : 'No'}"`,
                `"${formatDate(u.createdAt) || ''}"`
            ];
            csvRows.push(row.join(','));
        });

        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('hidden', '');
        a.setAttribute('href', url);
        a.setAttribute('download', 'users_export.csv');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.success("Users exported successfully");
    };

    return (
        <div style={{ background: '#F0F4F8', minHeight: '100vh' }}>
            <Topbar title="User Management" subtitle="View, search, and manage all registered users" />
            <div className="content">
                <div className="screens-stack">

                    {/* 2.1 USER LIST */}
                    <div style={{ display: view === 'list' ? 'block' : 'none' }}>
                        {/* <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
                            <div className="screen-label" style={{ margin: 0 }}>2.1 — User List</div>
                            {isLoading && <div style={{ fontSize: 12, color: 'var(--blue)', fontWeight: 600 }}>Syncing...</div>}
                        </div> */}
                        <div className="card" style={{ padding: '0px', overflow: 'hidden', maxWidth: '100%' }}>
                            <div className="toolbar" style={{ padding: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div className="search-wrap" style={{ flex: 1, minWidth: '150px' }}>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                                    <input
                                        className="search-input"
                                        placeholder="Search by name or email address..."
                                        value={filters.search}
                                        onChange={(e) => handleFilterChange('search', e.target.value)}
                                    />
                                </div>
                                <div className="filters-group" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                    <select
                                        className="filter-select"
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                        style={{ width: '120px' }}
                                    >
                                        <option value="">All Statuses</option>
                                        <option value="1">Active</option>
                                        <option value="2">Inactive</option>
                                        <option value="0">Deleted</option>
                                    </select>
                                    <select
                                        className="filter-select"
                                        value={filters.type}
                                        onChange={(e) => handleFilterChange('type', e.target.value)}
                                        style={{ width: '110px' }}
                                    >
                                        <option value="">All Types</option>
                                        <option value="Free">Free</option>
                                        <option value="Premium">Premium</option>
                                    </select>
                                    <select
                                        className="filter-select"
                                        value={filters.sortBy}
                                        onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                                        style={{ width: '130px' }}
                                    >
                                        <option value="createdAt">Sort: Date</option>
                                        <option value="full_name">Sort: Name</option>
                                        <option value="updatedAt">Sort: Login</option>
                                    </select>
                                </div>
                                <div className="actions-group" style={{ display: 'flex', gap: '10px' }}>
                                    <button className="btn btn-primary" onClick={() => navigate("/users/create")} style={{ height: '38px', whiteSpace: 'nowrap' }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                                        New User
                                    </button>
                                    <button className="btn btn-outline" onClick={handleExportCSV} style={{ border: '1px solid #E8EDF3', height: '38px' }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
                                        Export
                                    </button>
                                </div>
                            </div>

                            <div style={{ overflowX: 'auto', width: '100%' }}>
                                <table className="tbl" style={{ minWidth: '800px' }}>
                                <thead>
                                    <tr>
                                        <th><input type="checkbox" style={{ width: 14, height: 14, accentColor: 'var(--blue)' }} /></th>
                                        <th>User</th><th>Account Type</th><th>Status</th><th>Signup Date</th><th>Last Login</th><th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usersList.length > 0 ? usersList.map((u) => (
                                        <tr key={u.id}>
                                            <td><input type="checkbox" style={{ width: 14, height: 14, accentColor: 'var(--blue)' }} /></td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                                                    <div style={{ width: 34, height: 34, borderRadius: '50%', background: u.status === 2 ? '#F5F5F5' : '#EEF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: u.status === 2 ? 'var(--grey)' : 'var(--blue)', flexShrink: 0, overflow: 'hidden' }}>
                                                        {(u.profile_image || u.image) ? (
                                                            <img src={u.profile_image || u.image} alt={u.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                        ) : (
                                                            getInitials(u.full_name)
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 600, fontSize: 13, color: u.status === 2 ? 'var(--grey)' : 'var(--navy)' }}>{u.full_name}</div>
                                                        <div style={{ fontSize: 11, color: 'var(--grey)' }}>{u.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td><span className={`badge badge-${typeColor[u.type] || typeColor[u.account_type] || 'free'}`}>{u.type || typeLabel[u.account_type] || 'Free'}</span></td>
                                            <td><span className={`badge badge-${statusColor[u.status] || 'active'}`}>{statusLabel[u.status] || 'Active'}</span></td>
                                            <td style={{ fontSize: 12, color: 'var(--grey)' }}>{formatDate(u.createdAt)}</td>
                                            <td style={{ fontSize: 12, color: 'var(--grey)' }}>{formatDate(u.updatedAt)}</td>
                                            {/* <td style={{ fontSize: 12, color: 'var(--grey)' }}>{u.provider_name || 'local'}</td> */}
                                            <td>
                                                <div className="action-btns">
                                                    <button className="action-btn" onClick={() => { setSelectedUser(u); setView('profile'); }} title="View Profile">
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                                                    </button>
                                                    <button className="action-btn" onClick={() => navigate(`/users/edit/${u.id}`)} title="Edit User">
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                                    </button>
                                                    <button className="action-btn" onClick={() => handleToggleUserStatus(u)} title={u.status === 1 ? 'Deactivate User' : 'Activate User'}>
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>
                                                    </button>
                                                    <button className="action-btn danger" onClick={() => setUserToDelete(u)} title="Delete User">
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" /></svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: 'var(--grey)' }}>
                                                {isLoading ? 'Syncing users...' : 'No users found matching your criteria.'}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            </div>

                            <div className="pagination" style={{ padding: '20px 15px', display: 'flex', flexWrap: 'wrap', gap: '15px', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div className="page-info" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                    <span>Showing {meta.total > 0 ? (meta.currentPage - 1) * meta.limit + 1 : 0}–{Math.min(meta.currentPage * meta.limit, meta.total)} of {meta.total} users</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '13px', color: 'var(--grey)' }}>Rows per page:</span>
                                        <select 
                                            className="filter-select" 
                                            value={filters.limit} 
                                            onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                                            style={{ width: '70px', padding: '4px 8px', height: '30px', fontSize: '13px' }}
                                        >
                                            {[10, 20, 50, 100, ...(meta.total > 0 && ![10, 20, 50, 100].includes(meta.total) ? [meta.total] : [])].map(val => (
                                                <option key={val} value={val}>{val}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="page-btns">
                                    <button
                                        className="page-btn"
                                        disabled={meta.currentPage === 1}
                                        onClick={() => handleFilterChange('page', meta.currentPage - 1)}
                                    >‹</button>

                                    {[...Array(meta.totalPages)].map((_, i) => {
                                        const p = i + 1;
                                        if (p === 1 || p === meta.totalPages || (p >= meta.currentPage - 1 && p <= meta.currentPage + 1)) {
                                            return (
                                                <div
                                                    key={p}
                                                    className={`page-btn ${meta.currentPage === p ? 'active' : ''}`}
                                                    onClick={() => handleFilterChange('page', p)}
                                                >
                                                    {p}
                                                </div>
                                            );
                                        } else if (p === meta.currentPage - 2 || p === meta.currentPage + 2) {
                                            return <div key={p} className="page-btn" style={{ cursor: 'default' }}>...</div>;
                                        }
                                        return null;
                                    })}

                                    <button
                                        className="page-btn"
                                        disabled={meta.currentPage === meta.totalPages}
                                        onClick={() => handleFilterChange('page', meta.currentPage + 1)}
                                    >›</button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 2.2 USER PROFILE VIEW + 2.3 USER ACTIONS */}
                    {view === 'profile' && selectedUser && (
                        <div>
                            <div className="screen-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'end' }}>
                                {/* <span>2.2 — User Profile View &nbsp;·&nbsp; 2.3 — User Actions</span> */}
                                <button onClick={() => { setView('list'); setSelectedUser(null); }} style={{ background: 'none', border: 'none', color: 'var(--blue)', fontWeight: 600, cursor: 'pointer', fontSize: 12}}>✕ Close Profile</button>
                            </div>
                            <div className="card">
                                <div className="profile-layout">
                                    {/* LEFT */}
                                    <div className="profile-sidebar">
                                        <div className="profile-avatar-lg" style={{ overflow: 'hidden' }}>
                                            {(selectedUser.profile_image || selectedUser.image) ? (
                                                <img src={selectedUser.profile_image || selectedUser.image} alt={selectedUser.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                getInitials(selectedUser.full_name)
                                            )}
                                        </div>
                                        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 18, fontWeight: 700, color: 'var(--navy)', textAlign: 'center' }}>{selectedUser.full_name}</div>
                                        <div style={{ fontSize: 13, color: 'var(--grey)', textAlign: 'center' }}>{selectedUser.email}</div>
                                        <div style={{ marginTop: 4 }}>
                                            <span className={`badge badge-${typeColor[selectedUser.type] || typeColor[selectedUser.account_type] || 'free'}`}>{selectedUser.type || typeLabel[selectedUser.account_type] || 'Free'}</span>
                                            &nbsp;
                                            <span className={`badge badge-${statusColor[selectedUser.status] || 'active'}`}>{statusLabel[selectedUser.status] || 'Active'}</span>
                                        </div>
                                        <div style={{ width: '100%', height: 1, background: '#F0F4F8', margin: '16px 0' }} />
                                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--grey)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 12, alignSelf: 'flex-start' }}>User Actions</div>
                                        <div className="profile-actions">
                                            <button className="paction-btn" style={{ background: '#EEF6FF', color: 'var(--blue)' }} onClick={() => navigate(`/users/edit/${selectedUser.id}`)}>Edit Account Details</button>
                                            <button className="paction-btn" style={{ background: '#FFF0F0', color: 'var(--red)' }} onClick={() => setUserToDelete(selectedUser)}>Permanently Delete User</button>
                                            <button
                                                className="paction-btn"
                                                style={{ background: '#FFF4E0', color: '#B8860B' }}
                                                onClick={() => handleToggleUserStatus(selectedUser)}
                                                disabled={isTogglingStatus}
                                            >
                                                {selectedUser.status === 1 ? 'Deactivate Account' : 'Activate Account'}
                                            </button>
                                            <button className="paction-btn" style={{ background: 'var(--light)', color: 'var(--dark)' }} onClick={() => setIsResetPasswordModalOpen(true)}>Reset Password</button>
                                            <button className="paction-btn" style={{ background: '#EEF6FF', color: 'var(--blue)' }} onClick={() => setIsNotificationModalOpen(true)}>Send Notification</button>
                                        </div>
                                    </div>

                                    {/* RIGHT */}
                                    <div style={{ padding: '20px 28px', flex: 1 }}>
                                        <div className="fsec-title">Profile Details</div>
                                        <div className="details-grid">
                                            {[
                                                ['Full Name', selectedUser.full_name],
                                                ['Email Address', selectedUser.email],
                                                ['Phone Number', selectedUser.phone_number || 'Not provided'],
                                                ['Profile Image', (selectedUser.profile_image || selectedUser.image) ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                        <img 
                                                            src={selectedUser.profile_image || selectedUser.image} 
                                                            alt={selectedUser.full_name} 
                                                            style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', border: '1px solid #E8EDF3' }} 
                                                        />
                                                        <span style={{ fontSize: 12, color: 'var(--grey)' }}>Uploaded</span>
                                                    </div>
                                                ) : 'Not provided'],
                                                ['Account Status', statusLabel[selectedUser.status]],
                                                ['Account Type', selectedUser.type || typeLabel[selectedUser.account_type]],
                                                ['Provider', selectedUser.provider_name],
                                                ['Signup Date', formatDate(selectedUser.createdAt)],
                                                ['Last Updated', formatDate(selectedUser.updatedAt)],
                                                ['Email Verified', selectedUser.is_email_verified ? 'Yes' : 'No'],
                                            ].map(([label, value], i) => (
                                                <div className="field-row" key={i}>
                                                    <div className="field-label">{label}</div>
                                                    <div className="field-value" style={{ color: !value || value === 'Not provided' ? 'var(--grey)' : 'var(--navy)' }}>{value}</div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="fsec-title" style={{ marginTop: 32 }}>Activity History</div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                            {activitiesLoading ? (
                                                <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--grey)' }}>
                                                    <Spinner size="small" />
                                                </div>
                                            ) : activities.length === 0 ? (
                                                <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--grey)', fontSize: 14 }}>
                                                    No activities found
                                                </div>
                                            ) : (
                                                <>
                                                    {activities.map((a, i) => (
                                                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, paddingBottom: 12, borderBottom: i === activities.length - 1 ? 'none' : '1px solid #F8FAFC' }}>
                                                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: a.color || '#66B2FF', flexShrink: 0, marginTop: 5 }} />
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{ fontSize: 13, color: 'var(--dark)' }}>
                                                                    {a.action || a.description || a.text}
                                                                    {a.details && <span style={{ color: 'var(--grey)' }}> — {a.details}</span>}
                                                                </div>
                                                                <div style={{ fontSize: 11, color: 'var(--grey)', marginTop: 2 }}>{a.updatedAt ? formatDate(a.updatedAt) : (a.timestamp ? formatDate(a.timestamp) : a.time)}</div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {activitiesMeta.totalPages > 1 && (
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 12, borderTop: '1px solid #F8FAFC' }}>
                                                            <span style={{ fontSize: 12, color: 'var(--grey)' }}>
                                                                Page {activitiesMeta.currentPage} of {activitiesMeta.totalPages}
                                                            </span>
                                                            <div style={{ display: 'flex', gap: 8 }}>
                                                                <button 
                                                                    disabled={activitiesMeta.currentPage === 1}
                                                                    onClick={() => {
                                                                        setActivitiesPage(activitiesMeta.currentPage - 1);
                                                                        fetchActivities(activitiesMeta.currentPage - 1);
                                                                    }}
                                                                    style={{
                                                                        padding: '6px 12px',
                                                                        fontSize: 12,
                                                                        background: activitiesMeta.currentPage === 1 ? '#F8FAFC' : 'var(--blue)',
                                                                        color: activitiesMeta.currentPage === 1 ? 'var(--grey)' : 'white',
                                                                        border: 'none',
                                                                        borderRadius: 4,
                                                                        cursor: activitiesMeta.currentPage === 1 ? 'not-allowed' : 'pointer'
                                                                    }}
                                                                >
                                                                    Previous
                                                                </button>
                                                                <button 
                                                                    disabled={activitiesMeta.currentPage >= activitiesMeta.totalPages}
                                                                    onClick={() => {
                                                                        setActivitiesPage(activitiesMeta.currentPage + 1);
                                                                        fetchActivities(activitiesMeta.currentPage + 1);
                                                                    }}
                                                                    style={{
                                                                        padding: '6px 12px',
                                                                        fontSize: 12,
                                                                        background: activitiesMeta.currentPage >= activitiesMeta.totalPages ? '#F8FAFC' : 'var(--blue)',
                                                                        color: activitiesMeta.currentPage >= activitiesMeta.totalPages ? 'var(--grey)' : 'white',
                                                                        border: 'none',
                                                                        borderRadius: 4,
                                                                        cursor: activitiesMeta.currentPage >= activitiesMeta.totalPages ? 'not-allowed' : 'pointer'
                                                                    }}
                                                                >
                                                                    Next
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* DELETE CONFIRMATION MODAL */}
            {userToDelete && (
                <div style={{ 
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
                    background: 'rgba(10, 25, 48, 0.4)', backdropFilter: 'blur(4px)',
                    zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
                }}>
                    <div className="card" style={{ maxWidth: 400, padding: 30, textAlign: 'center', animation: 'slideUp 0.3s ease-out' }}>
                        <div style={{ 
                            width: 60, height: 60, background: '#FFF0F0', borderRadius: '50%', 
                            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px'
                        }}>
                            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2.2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                        </div>
                        <h3 style={{ fontSize: 20, color: 'var(--navy)', marginBottom: 12, fontFamily: "'Space Grotesk', sans-serif" }}>Confirm User Deletion</h3>
                        <p style={{ fontSize: 14, color: 'var(--grey)', lineHeight: 1.6, marginBottom: 28 }}>
                            Are you sure you want to delete <strong>{userToDelete.full_name}</strong>? This action cannot be undone and will permanently remove all associated data.
                        </p>
                        <div style={{ display: 'flex', gap: 12 }}>
                            <button className="btn btn-outline" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setUserToDelete(null)}>Cancel</button>
                            <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center', background: 'var(--red)', borderColor: 'var(--red)' }} onClick={handleDeleteConfirmed}>Confirm Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* RESET PASSWORD MODAL */}
            {isResetPasswordModalOpen && selectedUser && (
                <div style={{ 
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
                    background: 'rgba(10, 25, 48, 0.4)', backdropFilter: 'blur(4px)',
                    zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
                }}>
                    <div className="card" style={{ maxWidth: 400, width: '100%', padding: 30, animation: 'slideUp 0.3s ease-out', position: 'relative' }}>
                        <button onClick={closeResetModal} style={{ position: 'absolute', top: 15, right: 15, background: 'none', border: 'none', fontSize: 18, color: 'var(--grey)', cursor: 'pointer' }}>✕</button>
                        <h3 style={{ fontSize: 20, color: 'var(--navy)', marginBottom: 20, fontFamily: "'Space Grotesk', sans-serif", textAlign: 'center' }}>Reset Password</h3>
                        
                        {resetMessage && (
                            <div style={{
                                padding: '10px 14px', borderRadius: 8, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10,
                                background: resetMessage.type === 'error' ? 'rgba(255,59,48,0.08)' : 'rgba(52,199,89,0.08)',
                                border: `1px solid ${resetMessage.type === 'error' ? 'rgba(255,59,48,0.3)' : 'rgba(52,199,89,0.3)'}`
                            }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={resetMessage.type === 'error' ? '#FF3B30' : '#34C759'} strokeWidth="2" style={{ flexShrink: 0 }}>
                                    {resetMessage.type === 'error' ? (
                                        <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>
                                    ) : (
                                        <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></>
                                    )}
                                </svg>
                                <span style={{ fontSize: 13, color: resetMessage.type === 'error' ? '#FF3B30' : '#34C759', fontWeight: 500, lineHeight: 1.4 }}>
                                    {resetMessage.text}
                                </span>
                            </div>
                        )}

                        {resetStep === 1 && (
                            <form onSubmit={handleForgotPassword}>
                                <p style={{ fontSize: 14, color: 'var(--grey)', marginBottom: 20, textAlign: 'center', lineHeight: 1.5 }}>
                                    Send a password reset verification code to <strong>{selectedUser.email}</strong>.
                                </p>
                                <button type="submit" disabled={isResetLoading} style={{ ...modalButtonStyle, marginTop: 0 }}>
                                    {isResetLoading ? "Sending Code..." : "Send Verification Code"}
                                </button>
                            </form>
                        )}

                        {resetStep === 2 && (
                            <form onSubmit={handleVerify}>
                                <p style={{ fontSize: 14, color: 'var(--grey)', marginBottom: 20, textAlign: 'center', lineHeight: 1.5 }}>
                                    We've sent a code to <br/><strong style={{ color: 'var(--blue)' }}>{selectedUser.email}</strong>
                                </p>
                                <div style={{ display: 'flex', gap: 8, marginBottom: 20, justifyContent: 'center' }}>
                                    {resetOtp.map((data, index) => (
                                        <input
                                            key={index}
                                            type="text"
                                            maxLength="1"
                                            style={{
                                                width: 40, height: 48,
                                                background: '#F8FAFC',
                                                border: '1.5px solid #E8EDF3',
                                                borderRadius: 8, fontSize: 18, fontWeight: 700,
                                                color: 'var(--navy)', textAlign: 'center', outline: 'none',
                                                fontFamily: "'Space Grotesk', sans-serif",
                                                padding: 0
                                            }}
                                            value={data}
                                            onChange={e => handleOtpChange(e.target, index)}
                                            onKeyDown={e => handleKeyDown(e, index)}
                                            onFocus={e => e.target.select()}
                                        />
                                    ))}
                                </div>
                                <button type="submit" disabled={isResetLoading} style={{ ...modalButtonStyle, marginTop: 0 }}>
                                    {isResetLoading ? "Verifying..." : "Verify Code"}
                                </button>
                                <div style={{ textAlign: 'center', marginTop: 15 }}>
                                    <span style={{ fontSize: 13, color: 'var(--grey)' }}>Didn't receive the code? </span>
                                    <button
                                        type="button"
                                        onClick={handleResend}
                                        disabled={resetTimer > 0 || isResetLoading}
                                        style={{
                                            background: 'none', border: 'none', padding: 0,
                                            fontSize: 13, fontWeight: 700,
                                            color: (resetTimer > 0 || isResetLoading) ? 'var(--grey)' : 'var(--blue)',
                                            cursor: (resetTimer > 0 || isResetLoading) ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        Resend {resetTimer > 0 && `(${formatTimer(resetTimer)})`}
                                    </button>
                                </div>
                            </form>
                        )}

                        {resetStep === 3 && (
                            <form onSubmit={handleResetPassword}>
                                <p style={{ fontSize: 14, color: 'var(--grey)', marginBottom: 20, textAlign: 'center', lineHeight: 1.5 }}>
                                    Please set a new, secure password for {selectedUser.full_name}.
                                </p>
                                
                                <div style={{ marginBottom: 15 }}>
                                    <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--grey)', display: 'block', marginBottom: 6 }}>New Password</label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type={showPass ? 'text' : 'password'}
                                            value={newPassword}
                                            onChange={e => setNewPassword(e.target.value)}
                                            placeholder="Min. 8 characters"
                                            style={{ ...modalInputStyle, paddingRight: 40 }}
                                            required
                                        />
                                        <button type="button" onClick={() => setShowPass(!showPass)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--grey)' }}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                                                {showPass
                                                    ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></>
                                                    : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
                                                }
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                <div style={{ marginBottom: 20 }}>
                                    <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--grey)', display: 'block', marginBottom: 6 }}>Confirm Password</label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type={showConfirmPass ? 'text' : 'password'}
                                            value={confirmPassword}
                                            onChange={e => setConfirmPassword(e.target.value)}
                                            placeholder="Re-enter password"
                                            style={{ ...modalInputStyle, paddingRight: 40 }}
                                            required
                                        />
                                        <button type="button" onClick={() => setShowConfirmPass(!showConfirmPass)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--grey)' }}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                                                {showConfirmPass
                                                    ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></>
                                                    : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
                                                }
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                <button type="submit" disabled={isResetLoading} style={{ ...modalButtonStyle, marginTop: 0 }}>
                                    {isResetLoading ? "Resetting..." : "Reset Password"}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}


            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
                
                .details-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px 20px; }
                .pagination { display: flex; justify-content: space-between; align-items: center; padding: 20px; }
                .page-info { text-align: left; }
                .page-btns { margin: 0; }

                @media (max-width: 1024px) {
                    .toolbar { flex-direction: column !important; align-items: stretch !important; gap: 12px !important; }
                    .filters-group { flex-wrap: wrap !important; width: 100% !important; }
                    .filters-group select { flex: 1 !important; min-width: 120px !important; width: auto !important; }
                    .actions-group { width: 100% !important; }
                    .actions-group button { flex: 1 !important; }
                    
                    .pagination { flex-direction: column !important; align-items: center !important; gap: 12px !important; }
                    .page-info { width: 100% !important; text-align: center !important; }
                    .page-btns { margin: 0 auto !important; }
                }

                @media (max-width: 768px) {
                    .profile-layout { flex-direction: column !important; }
                    .profile-sidebar { border-right: none !important; border-bottom: 1px solid #F0F4F8 !important; width: 100% !important; padding: 25px !important; }
                    .field-row { flex-direction: column !important; align-items: flex-start !important; gap: 4px !important; }
                    .details-grid { grid-template-columns: 1fr !important; }
                    .content { padding: 15px !important; }
                }

                .tbl th, .tbl td { white-space: nowrap; }
                .content { max-width: 100vw; overflow-x: hidden; }
            `}</style>
            {(isLoading || isTogglingStatus || isResetLoading || isDeleting) && <Spinner />}

            {/* SEND NOTIFICATION MODAL */}
            {isNotificationModalOpen && selectedUser && (
                <div style={{ 
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
                    background: 'rgba(10, 25, 48, 0.4)', backdropFilter: 'blur(4px)',
                    zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
                }}>
                    <div className="card" style={{ maxWidth: 450, width: '100%', padding: 30, animation: 'slideUp 0.3s ease-out', position: 'relative' }}>
                        <button onClick={closeNotificationModal} style={{ position: 'absolute', top: 15, right: 15, background: 'none', border: 'none', fontSize: 18, color: 'var(--grey)', cursor: 'pointer' }}>✕</button>
                        <h3 style={{ fontSize: 20, color: 'var(--navy)', marginBottom: 20, fontFamily: "'Space Grotesk', sans-serif", textAlign: 'center' }}>Send Notification to {selectedUser.full_name}</h3>
                        
                        <form onSubmit={handleSendNotification}>
                            <div style={{ marginBottom: 20 }}>
                                <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--grey)', display: 'block', marginBottom: 8 }}>Notification Title</label>
                                <input
                                    type="text"
                                    value={notificationTitle}
                                    onChange={e => setNotificationTitle(e.target.value)}
                                    placeholder="Enter notification title"
                                    style={{ ...modalInputStyle }}
                                    required
                                />
                            </div>

                            <div style={{ marginBottom: 20 }}>
                                <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--grey)', display: 'block', marginBottom: 8 }}>Message</label>
                                <textarea
                                    value={notificationMessage}
                                    onChange={e => setNotificationMessage(e.target.value)}
                                    placeholder="Enter notification message"
                                    style={{ 
                                        ...modalInputStyle, 
                                        minHeight: 100, 
                                        fontFamily: "'Space Grotesk', sans-serif",
                                        resize: 'vertical'
                                    }}
                                    required
                                />
                            </div>

                            <button 
                                type="submit" 
                                disabled={isSendingNotification} 
                                style={{ 
                                    ...modalButtonStyle, 
                                    marginTop: 0,
                                    background: isSendingNotification ? 'rgba(102,178,255,0.5)' : 'var(--blue)'
                                }}
                            >
                                {isSendingNotification ? (
                                    <>
                                        <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid white', animation: 'spin 0.8s linear infinite' }} />
                                        Sending...
                                    </>
                                ) : (
                                    'Send Notification'
                                )}
                            </button>

                            <button 
                                type="button" 
                                onClick={closeNotificationModal}
                                disabled={isSendingNotification}
                                style={{ 
                                    ...modalButtonStyle, 
                                    marginTop: 10,
                                    background: 'transparent',
                                    color: 'var(--blue)',
                                    border: '1.5px solid var(--blue)'
                                }}
                            >
                                Cancel
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;
